import React, { createContext, useContext, useMemo } from 'react';
import type { DataRepository } from '@/types/repository';
import { isAndroidPlatform } from '@/utils/platform';
import { androidRepository } from '@/lib/repository/androidRepository';
import { webRepository } from '@/lib/repository/webRepository';

interface PlatformContextValue {
  isAndroidApp: boolean;
  isWebCompanion: boolean;
  repository: DataRepository;
}

const PlatformContext = createContext<PlatformContextValue | null>(null);

export function PlatformProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo(() => {
    const isAndroid = isAndroidPlatform();
    return {
      isAndroidApp: isAndroid,
      isWebCompanion: !isAndroid,
      repository: isAndroid ? androidRepository : webRepository,
    };
  }, []);

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatform(): PlatformContextValue {
  const context = useContext(PlatformContext);
  if (!context) {
    // Fallback if rendered outside provider
    const isAndroid = isAndroidPlatform();
    return {
      isAndroidApp: isAndroid,
      isWebCompanion: !isAndroid,
      repository: isAndroid ? androidRepository : webRepository,
    };
  }
  return context;
}

export function useRepository(): DataRepository {
  return usePlatform().repository;
}
